import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { PlaceContext } from '@m4a3/api-client';
import PlaceDropPointSelector from './PlaceDropPointSelector';

interface PlaceDropPointModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Initial eircode to load */
  initialEircode?: string;
  /** Shared place context data */
  placeContext?: PlaceContext | null;
  /** Callback when a drop point is selected */
  onDropPointSelect?: (dropPointId: string, lat: number, lng: number, placeContext: PlaceContext) => void;
  /** Callback when data changes (for syncing with parent) */
  onDataChange?: (placeContext: PlaceContext) => void;
  /** Modal title */
  title?: string;
  /** Suggested drop point latitude and longitude */
  suggestedDropPointLatLon?: string;
}

const PlaceDropPointModal = ({
  isOpen,
  onClose,
  initialEircode,
  placeContext,
  onDropPointSelect,
  onDataChange,
  title = 'Delivery Spot Management',
  suggestedDropPointLatLon,
}: PlaceDropPointModalProps) => {
  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleDropPointSelect = (dropPointId: string, lat: number, lng: number, context: PlaceContext) => {
    onDropPointSelect?.(dropPointId, lat, lng, context);
    // Update the shared place context
    onDataChange?.(context);
  };

  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4" 
      style={{ zIndex: 99999 }}
      onClick={onClose}
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        style={{ zIndex: 99998 }}
      />
      
      {/* Modal */}
      <div className="flex items-center justify-center relative w-full" style={{ zIndex: 99999 }}>
        <div 
          className="relative w-full max-w-6xl bg-white rounded-lg shadow-xl flex flex-col"
          onClick={(e) => e.stopPropagation()}
          style={{ zIndex: 100000, height: '80vh' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 rounded-full transition-colors"
              title="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Content */}
          <div 
            className="p-4 flex-1 overflow-y-scroll relative"
            style={{ zIndex: 1, isolation: 'isolate' }}
          >
            <PlaceDropPointSelector
              initialEircode={initialEircode}
              onDropPointSelect={handleDropPointSelect}
              title=""
              subtitle=""
              enableManagement={true}
              isCompact={false}
              suggestedDropPointLatLon={suggestedDropPointLatLon}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal at document body level using portal
  return createPortal(modalContent, document.body);
};

export default PlaceDropPointModal;
